// src/pages/finance-portal/receipts/Receipts.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton, Tooltip,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import LinkIcon from '@mui/icons-material/Link';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_COLORS = {
    UPLOADED: { bg: '#dbeafe', color: '#1e40af' },
    PROCESSING: { bg: '#fef3c7', color: '#92400e' },
    PROCESSED: { bg: '#dcfce7', color: '#166534' },
    LINKED: { bg: '#e0e7ff', color: '#3730a3' },
    FAILED: { bg: '#fecaca', color: '#991b1b' },
};

export default function Receipts() {
    const [receipts, setReceipts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [matching, setMatching] = useState(false);
    const [processingIds, setProcessingIds] = useState(new Set());
    const [linkingIds, setLinkingIds] = useState(new Set());
    const fileInputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    const fetchReceipts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/receipts');
            if (res.data.success) {
                setReceipts(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load receipts');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

    const uploadFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                await axiosInstance.post('/api/finance/receipts/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }
            setSuccess(`Uploaded ${files.length} receipt(s)`);
            fetchReceipts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload receipt');
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = (e) => {
        uploadFiles(e.target.files);
        e.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        uploadFiles(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => setDragOver(false);

    const handleProcessOCR = async (id) => {
        setProcessingIds(prev => new Set([...prev, id]));
        try {
            await axiosInstance.post(`/api/finance/receipts/${id}/process-ocr`);
            setSuccess('OCR processing complete');
            fetchReceipts();
        } catch (err) {
            setError(err.response?.data?.message || 'OCR processing failed');
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleLink = async (id) => {
        setLinkingIds(prev => new Set([...prev, id]));
        try {
            await axiosInstance.post(`/api/finance/receipts/${id}/link`);
            setSuccess('Receipt linked to expense');
            fetchReceipts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to link receipt');
        } finally {
            setLinkingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleAutoMatch = async () => {
        setMatching(true);
        try {
            const res = await axiosInstance.post('/api/finance/receipts/match-transactions');
            if (res.data.success) {
                setSuccess(`Matched ${res.data.data?.matched || 0} receipt(s) to transactions`);
            }
            fetchReceipts();
        } catch (err) {
            setError(err.response?.data?.message || 'Auto-match failed');
        } finally {
            setMatching(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ReceiptIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Receipts</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Upload, OCR & match receipts</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" startIcon={matching ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AutoFixHighIcon />}
                            onClick={handleAutoMatch} disabled={matching}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Auto-Match
                        </Button>
                        <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchReceipts}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Upload Area */}
                <Paper
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        p: 4, mb: 3, borderRadius: '16px',
                        border: dragOver ? '2px dashed #22c55e' : '2px dashed #e2e8f0',
                        bgcolor: dragOver ? '#f0fdf4' : '#fff',
                        textAlign: 'center', cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: '#22c55e', bgcolor: '#f0fdf4' },
                    }}
                >
                    <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" onChange={handleFileSelect} style={{ display: 'none' }} />
                    {uploading ? (
                        <CircularProgress sx={{ color: ACCENT }} />
                    ) : (
                        <>
                            <CloudUploadIcon sx={{ fontSize: 48, color: dragOver ? ACCENT : '#94a3b8', mb: 1 }} />
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>
                                Drag & drop receipts here or click to browse
                            </Typography>
                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.8rem', mt: 0.5 }}>
                                Supports images and PDFs
                            </Typography>
                        </>
                    )}
                </Paper>

                {/* Receipts Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['File', 'Merchant', 'Date', 'Amount', 'Status', 'Linked To', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : receipts.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <ReceiptIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No receipts uploaded yet</Typography>
                                    </TableCell></TableRow>
                                ) : receipts.map(r => {
                                    const statusStyle = STATUS_COLORS[r.status] || { bg: '#f1f5f9', color: '#64748b' };
                                    return (
                                        <TableRow key={r.id} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {r.filename || r.file_name || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {r.merchant_name || r.ocr_data?.merchant || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(r.receipt_date || r.ocr_data?.date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                                                {r.amount || r.ocr_data?.amount ? formatCurrency(r.amount || r.ocr_data?.amount) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={r.status} size="small"
                                                    sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {r.expense_id ? `Expense #${r.expense_id}` : r.transaction_id ? `Txn #${r.transaction_id}` : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {(r.status === 'UPLOADED') && (
                                                        <Tooltip title="Process OCR">
                                                            <IconButton size="small" onClick={() => handleProcessOCR(r.id)}
                                                                disabled={processingIds.has(r.id)}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}
                                                            >
                                                                {processingIds.has(r.id) ? <CircularProgress size={16} /> : <DocumentScannerIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(r.status === 'PROCESSED') && !r.expense_id && !r.transaction_id && (
                                                        <Tooltip title="Link to Expense">
                                                            <IconButton size="small" onClick={() => handleLink(r.id)}
                                                                disabled={linkingIds.has(r.id)}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: ACCENT } }}
                                                            >
                                                                {linkingIds.has(r.id) ? <CircularProgress size={16} /> : <LinkIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>
        </Box>
    );
}
